import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

const privacySections = [
  {
    id: "general",
    title: "General Terms",
    content: [
      "By accessing or using the Platform or the Service, or by otherwise giving Us Your information, You confirm that You have the capacity to enter into a legally binding contract under Indian law, in particular, the Indian Contract Act, 1872, and have read, understood and agreed to the practices and policies outlined in this Privacy Policy and agree to be bound by the Privacy Policy.",
      "You hereby consent to Our collection, use, sharing, and disclosure of Your information as described in this Privacy Policy. We reserve the right to change, modify, add or delete portions of the terms of this Privacy Policy, at Our sole discretion, at any time.",
      "If You are accessing or using Services on the Site from an overseas location, You do so at Your own risk, and shall be solely liable for compliance with any applicable local laws.",
      "If You do not agree with any of the terms and conditions of this Privacy Policy, please do not proceed further to use this Site or any Services.",
    ],
  },
  {
    id: "collection",
    title: "1. What Information About You is Collected?",
    content: [
      "Information You Give Us: We receive and store any information You enter on Our Platform or provide Us in any other way. When You register on the Site, we collect registration details such as phone number, name, geographical address and email address. We verify Your phone number with the help of a one-time password sent to Your phone number.",
      "We may also collect sensitive personal data or information (\"SPDI\") about You when You use Our Service(s) on the Platform. This information includes health information We receive from You or, on Your behalf, such as information or records relating to Your medical or health history, health status and laboratory testing results.",
      "Information from Other Sources: We might receive information about You such as order details, Your details shared with Our partners etc. from other sources including from Our partners, advertisers or third parties registered on the Platform.",
      "Cookies: We utilize \"cookies\" and other tracking technologies. A \"cookie\" is a small text file that may be used to collect information about activity on the Site. Most browsers/mobile settings allow You to control cookies, including whether or not to accept them.",
      "Automatic Information: We receive and store certain types of information whenever You interact with Us. We may also receive/store information about Your location and your mobile device, including a unique identifier for Your device.",
    ],
  },
  {
    id: "use",
    title: "2. How Do We Use the Information We Collect?",
    items: [
      "To carry out our obligations arising from your requests for the Products and Services on the Platform",
      "To operate and improve the Platform in order to foster a positive user experience",
      "To process and deliver Your order with Us",
      "To enable Your access to Our Site to purchase products and provide You Services",
      "To enable You to initiate consultation with Medical Practitioners or facilitating a diagnostic test",
      "Analysing data, tracking trends, building algorithms, creating databases for rating systems, recommendations engines, etc.",
      "Responding to Your requests, customising Your orders, improving Our Platform or communicating with You",
      "To comply with applicable law",
      "To investigate, prevent, or take action regarding illegal activities, suspected fraud, or violations of Our Terms",
      "To contact You, by way of SMS, email and phone calls, to record Your valuable feedback on Our products and Services",
      "To personalize services using cookies and automatically collected data",
    ],
  },
  {
    id: "sharing",
    title: "3. Do We Share the Information We Receive?",
    content: [
      "Retail partners: Some of Your personal information or SPDI will be shared with affiliated retailers, the Retail Pharmacies who supply and service Your order and with the Medical Practitioners.",
      "Third Party Service Providers: Some of Your personal information or SPDI may be shared with third party service providers for fulfilment of Services and delivery of Your order(s).",
      "Our Legal obligations: We may release account and other personal information when required to comply with law or protect rights, property, or safety.",
      "Sharing upon merger or amalgamation: Any third party to which We transfer or sell Our assets, merge or consolidate with, will have the right to continue to use the information in accordance with this Privacy Policy.",
      "Transfer to third parties and outside India: Subject to applicable law, We may at Our sole discretion, transfer personal information and SPDI to any other body corporate that ensures at least the same level of data protection as is provided by Us.",
    ],
  },
  {
    id: "security",
    title: "4. How Secure Is Information About Me?",
    content: [
      "We maintain electronic, physical and procedural safeguards in connection with the collection, storage and disclosure of personal information (including SPDI). We use SSL, restrict access to authorized personnel under confidentiality obligations, and follow recognized information security standards.",
      "No employee or administrator will have knowledge of Your password. It is important for you to protect your account against unauthorized access. If You suspect any unauthorized use of Your account, notify Us immediately.",
      "Under certain circumstances, we may disclose information without prior consent where required by government agencies or otherwise mandated under law.",
    ],
  },
  {
    id: "access",
    title: "5. What Information Can I Access?",
    content: [
      "The Platform gives You access to a broad range of information about Your account and Your interactions with the Platform for the limited purpose of viewing and, in certain cases, modifying, deleting information provided on the Site. You may rectify any inaccurate or deficient personal information or SPDI, as feasible, and opt in or out of optional services.",
    ],
  },
  {
    id: "children",
    title: "6. Are Children Allowed to Use the Platform?",
    content: [
      "Use of the Site is available only to persons who can form a legally binding contract under the Indian Contract Act, 1872. If You are under 18 years of age, please do not use or access the Service(s). If we learn that a person under 18 has used or accessed the Platform, we will delete such information. Parents/guardians may contact us at medisys.customercare@gmail.com to request deletion.",
    ],
  },
  {
    id: "thirdparty",
    title: "7. Third-party Links",
    content: [
      "The Site may include hyperlinks to external websites, advertisements, applications, content or resources (\"Third Party Links\"). We have no control over such Third Party Links and are not responsible for any collection or disclosure of Your information by any external sites. We recommend reviewing the third party's privacy policy before engaging.",
    ],
  },
  {
    id: "retention",
    title: "8. Retention of Information",
    content: [
      "Your SPDI in Our possession or control is destroyed and/or anonymized when the purposes for which it was collected have been fulfilled and retention is no longer necessary under applicable law. We may retain de-identified or aggregated data for analytics and business purposes as permitted by law.",
    ],
  },
  {
    id: "changes",
    title: "9. Changes to Your Information",
    content: [
      "You may review, correct, update, change information You have provided by logging into Your account or by writing to Us. If information becomes unverifiable or incorrect, We may be unable to provide You access to the Site or Services. We reserve the right to verify and authenticate Your identity and personal information.",
    ],
  },
  {
    id: "notices",
    title: "10. Notices",
    isContact: true,
  },
  {
    id: "misc",
    title: "11. Miscellaneous",
    content: [
      "Disclaimer: While We are committed to protecting Your privacy, we do not promise that Your information or communications will always remain private. You assume all responsibility and risk for Your use of the Site and the information You post or access.",
      "Indemnity: You agree to indemnify Us in any suit or dispute by any third party arising out of disclosure of information by You to third parties either through Our Site or otherwise.",
      "Severability: Each clause of this Privacy Policy shall be separate and severable. If any clause is held void, the remaining clauses shall continue in full force.",
    ],
  },
];

const PrivacyPolicy = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [activeSection, setActiveSection] = useState(null);

  return (
    <div className="min-h-screen bg-[#f8fafa] font-poppins">
      <Helmet>
        <title>Privacy Policy | MediSYS Pharmacy</title>
        <meta name="description" content="Read MediSYS Pharmacy's Privacy Policy to understand how we collect, use, process, and protect your information." />
        <meta name="keywords"    content="privacy policy, MediSYS, data protection, personal information, SPDI, IT Act" />
        <meta name="robots"      content="index, follow" />
        <meta property="og:title"       content="Privacy Policy | MediSYS Pharmacy" />
        <meta property="og:description" content="How MediSYS Pharmacy collects, uses, and protects your data." />
      </Helmet>

      {/* ── Hero strip ── */}
      <div className="bg-[#162c2a] px-5 sm:px-8 lg:px-14 py-10 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-6 h-[2px] bg-[#ff601e] rounded-full" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5fbdb3]">Legal</span>
          </div>
          <h1 className="font-bold text-[28px] sm:text-[36px] text-white leading-tight">
            Privacy Policy
          </h1>
          <p className="mt-2 text-[13px] text-white/50 max-w-[500px]">
            This Privacy Policy describes what information MediSYS Pharmacy may collect from you, how we use it, and how we protect it.
          </p>
          <div className="flex items-center gap-2 mt-4">
            <span className="text-[11px] text-white/35">Effective date:</span>
            <span className="text-[11px] font-medium text-white/60">2024</span>
          </div>
        </div>
      </div>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* ── Intro block ── */}
        <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.07)] p-6 sm:p-8 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e4a46" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <h2 className="font-bold text-[17px] text-[#1e4a46]">Your Privacy Matters</h2>
          </div>
          <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify mb-3">
            This Privacy Policy describes what information <span className="font-semibold text-[#1e4a46]">MediSYS Pharmacy</span> ("Company") may collect from a user on or through our website{" "}
            <span className="font-semibold text-[#1e4a46]">https://medisyspharmacy.com/</span>, including facilitation of purchase of pharmaceutical drugs, consultation with registered medical practitioners, and delivery of pharmaceutical products.
          </p>
          <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">
            By clicking 'Continue' during sign-up, by using Our Platform, or by using the Services, you confirm that You have read, understood, and agree with the privacy practices described in this Privacy Policy.
          </p>
          <div className="mt-4 flex items-center gap-2 text-[11px] text-[#8faba9]">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Published in compliance with IT Act, 2000 · SPDI Rules, 2011 · Intermediaries Guidelines, 2021
          </div>
        </div>

        {/* ── Sections ── */}
        <div className="flex flex-col gap-3">
          {privacySections.map((section, idx) => {
            const isOpen = activeSection === idx;
            return (
              <div
                key={section.id}
                className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden ${
                  isOpen
                    ? "border-[#5fbdb3] shadow-[0_4px_18px_rgba(22,44,42,0.1)]"
                    : "border-[#e2eaea] shadow-[0_2px_8px_rgba(22,44,42,0.05)] hover:border-[#c5d5d4]"
                }`}
              >
                <button
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  onClick={() => setActiveSection(isOpen ? null : idx)}
                >
                  <div className="flex items-center gap-3">
                    <span className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold transition-colors duration-200 ${
                      isOpen ? "bg-[#1e4a46] text-white" : "bg-[#eaf4f3] text-[#1e4a46]"
                    }`}>
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <h3 className={`text-[13px] sm:text-[14px] font-semibold transition-colors duration-200 ${
                      isOpen ? "text-[#1e4a46]" : "text-[#374747]"
                    }`}>
                      {section.title}
                    </h3>
                  </div>
                  <span className={`flex-shrink-0 w-7 h-7 rounded-xl flex items-center justify-center transition-all duration-200 ${
                    isOpen ? "bg-[#1e4a46] text-white rotate-180" : "bg-[#f1f5f5] text-[#8faba9]"
                  }`}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </span>
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 border-t border-[#f1f5f5] pt-4">
                    <div className="pl-10 flex flex-col gap-3">

                      {/* Regular paragraphs */}
                      {section.content?.map((para, i) => (
                        <div key={i} className="flex gap-3">
                          <span className="flex-shrink-0 w-1 self-stretch bg-[#ff601e] rounded-full opacity-60" />
                          <p className="text-[13px] sm:text-[14px] text-[#374747] leading-[1.85] text-justify">{para}</p>
                        </div>
                      ))}

                      {/* Bullet list */}
                      {section.items && (
                        <ul className="flex flex-col gap-2">
                          {section.items.map((item, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-[13px] sm:text-[14px] text-[#374747] leading-relaxed">
                              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#5fbdb3] mt-2" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Contact section */}
                      {section.isContact && (
                        <div className="bg-[#eaf4f3] border border-[#d4ecea] rounded-xl p-4 flex flex-col gap-2">
                          <p className="text-[13px] text-[#374747] mb-1">
                            If You have any concern about privacy or grievances on the Site, please contact Us:
                          </p>
                          {[
                            { label: "Name",    value: "Mr. Akshay Mitra" },
                            { label: "Address", value: "1-8-44/4, Flat No 2A, Street No 12, Chikkadpally, Hyderabad - 500020" },
                            { label: "Phone",   value: "+91 7799923501" },
                            { label: "E-mail",  value: "medisys.customercare@gmail.com" },
                          ].map((row) => (
                            <div key={row.label} className="flex items-start gap-2 text-[13px]">
                              <span className="font-semibold text-[#1e4a46] min-w-[60px] flex-shrink-0">{row.label}:</span>
                              <span className="text-[#374747]">{row.value}</span>
                            </div>
                          ))}
                          <p className="text-[12px] text-[#607d7b] mt-1 italic">
                            We shall endeavour to resolve Your grievances within one month from the date of receipt.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="mt-8 bg-[#162c2a] rounded-2xl p-6 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-bold text-[15px] sm:text-[17px] text-white mb-1">Questions about your privacy?</p>
              <p className="text-[12px] text-white/50">Reach out to our support team — we're here to help.</p>
            </div>
            <Link
              to="/contact-us"
              className="flex-shrink-0 flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-semibold text-[13px] px-5 h-10 rounded-xl transition-all duration-200 shadow-[0_3px_12px_rgba(255,96,30,0.35)] hover:-translate-y-0.5 whitespace-nowrap"
            >
              Contact Us
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;